import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { runBigQueryQuery } from "../bigquery/BigQueryClient";
import { computeXmr, type Xmr } from "../metrics/xmr";
import { computeKalman } from "../metrics/kalman";
import XmrChart from "../components/charts/XmrChart";
import EChart from "../components/charts/EChart";
import { defaultGrid, tooltipCss } from "../components/charts/chartDefaults";
import HeroHeader from "../components/AudienceHeroHeader";
import type { Timeseries } from "../types";
import type { EChartsOption } from "echarts";

interface BudgetSummary {
  budget_name: string;
  audience_names: string | null;
  max_date: string;
  revenue_eur: number;
  prev_revenue_eur: number;
}

interface D1Metrics {
  d1_ecpx_eur: number | null;
  prev_d1_ecpx_eur: number | null;
  d1_roas: number | null;
  prev_d1_roas: number | null;
}

interface DailyD1Row {
  bid_date: string;
  revenue_eur: number;
  d1_ap_conversions: number;
  d1_ap_customer_revenue_eur: number;
  d7_ap_conversions: number;
  d7_ap_customer_revenue_eur: number;
  d30_ap_conversions: number;
  d30_ap_customer_revenue_eur: number;
  supply_cost_eur: number;
}

const formatEUR = (value: number) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(value);

const formatEURCpx = (value: number) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

function formatChange(current: number, prev: number): { label: string; up: boolean } | null {
  if (!prev || prev <= 0) return null;
  const pct = Math.round(((current - prev) / prev) * 100);
  return { label: `${pct >= 0 ? "▲" : "▼"} ${pct >= 0 ? "+" : ""}${pct}%`, up: pct >= 0 };
}

function formatChangeCpx(current: number | null, prev: number | null): { label: string; up: boolean } | null {
  if (current == null || prev == null || prev <= 0) return null;
  const pct = Math.round(((current - prev) / prev) * 100);
  return { label: `${pct >= 0 ? "▲" : "▼"} ${pct >= 0 ? "+" : ""}${pct}%`, up: pct <= 0 };
}

function formatChangeRoas(current: number | null, prev: number | null): { label: string; up: boolean } | null {
  if (current == null || prev == null || prev <= 0) return null;
  const pct = Math.round(((current - prev) / prev) * 100);
  return { label: `${pct >= 0 ? "▲" : "▼"} ${pct >= 0 ? "+" : ""}${pct}%`, up: pct >= 0 };
}

function formatDateRange(maxDateStr: string): string {
  const end = new Date(maxDateStr);
  const start = new Date(end);
  start.setDate(start.getDate() - 6);
  const fmt = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric" });
  return `${fmt.format(start)} – ${fmt.format(end)}`;
}

function addDays(dateStr: string, days: number): string {
  const d = new Date(dateStr + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function getChartMaxDate(): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - 2);
  return d.toISOString().slice(0, 10);
}

function buildSummarySQL(id: string): string {
  if (!/^\d+$/.test(id)) throw new Error("Invalid budget ID");
  return `
    WITH pool_currency AS (
      SELECT MAX(c.rate) AS rate
      FROM \`martin-test-datalab.postgres.campaigns\` camp
      JOIN \`martin-test-datalab.postgres.billing_accounts\` ba ON camp.billing_account_id = ba.id
      JOIN \`martin-test-datalab.postgres.currencies\` c ON ba.currency_id = c.id
      WHERE camp.budget_pool_id = ${id}
    ),
    latest AS (
      SELECT MAX(DATE(start_date)) AS max_date
      FROM \`martin-test-datalab.postgres.order_line_items\`
      WHERE budget_used > 0
        AND budget_pool_id = ${id}
        AND DATE(start_date) >= DATE_SUB(CURRENT_DATE(), INTERVAL 30 DAY)
        AND DATE(start_date) <= DATE_SUB(CURRENT_DATE(), INTERVAL 2 DAY)
    ),
    audiences AS (
      SELECT STRING_AGG(DISTINCT a.name, ', ') AS audience_names
      FROM \`martin-test-datalab.postgres.campaigns\` c
      JOIN \`martin-test-datalab.postgres.audiences\` a ON c.audience_id = a.id
      WHERE c.budget_pool_id = ${id}
    ),
    olis AS (
      SELECT
        oli.budget_used / 1000000.0 / COALESCE(pc.rate, 1.0) AS revenue_eur,
        DATE(oli.start_date) AS day,
        l.max_date
      FROM \`martin-test-datalab.postgres.order_line_items\` oli
      CROSS JOIN pool_currency pc
      CROSS JOIN latest l
      WHERE oli.budget_pool_id = ${id}
        AND oli.budget_used > 0
        AND DATE(oli.start_date) >= DATE_SUB(l.max_date, INTERVAL 14 DAY)
    )
    SELECT
      bp.name AS budget_name,
      l.max_date,
      aud.audience_names,
      SUM(CASE WHEN o.day >= DATE_SUB(o.max_date, INTERVAL 7 DAY) THEN o.revenue_eur ELSE 0 END) AS revenue_eur,
      SUM(CASE WHEN o.day < DATE_SUB(o.max_date, INTERVAL 7 DAY) THEN o.revenue_eur ELSE 0 END) AS prev_revenue_eur
    FROM olis o
    CROSS JOIN \`martin-test-datalab.postgres.budget_pools\` bp
    CROSS JOIN latest l
    CROSS JOIN audiences aud
    WHERE bp.id = ${id}
    GROUP BY bp.name, l.max_date, aud.audience_names
  `;
}

function buildCohortedDailySQL(id: string, maxDate: string): string {
  if (!/^\d+$/.test(id)) throw new Error("Invalid budget ID");
  const start = addDays(maxDate, -27);
  return `
    SELECT
      bid_date,
      SUM(revenue_eur) AS revenue_eur,
      SUM(supply_cost_eur) AS supply_cost_eur,
      SUM(d1_ap_conversions) AS d1_ap_conversions,
      SUM(d1_ap_customer_revenue_eur) AS d1_ap_customer_revenue_eur,
      SUM(d7_ap_conversions) AS d7_ap_conversions,
      SUM(d7_ap_customer_revenue_eur) AS d7_ap_customer_revenue_eur,
      SUM(d30_ap_conversions) AS d30_ap_conversions,
      SUM(d30_ap_customer_revenue_eur) AS d30_ap_customer_revenue_eur
    FROM \`remerge-dwh.ops.apro_campaign_level_cohorted_performance\`
    WHERE budget_id = ${id}
      AND bid_date >= '${start}' AND bid_date <= '${maxDate}'
    GROUP BY bid_date
    ORDER BY bid_date
  `;
}

function computeD1FromDaily(rows: DailyD1Row[], maxDate: string): D1Metrics {
  const split = addDays(maxDate, -6);
  let cr = 0,
    pr = 0,
    cc = 0,
    pc = 0,
    car = 0,
    par = 0;
  for (const row of rows) {
    if (row.bid_date >= split) {
      cr += row.revenue_eur;
      cc += row.d1_ap_conversions;
      car += row.d1_ap_customer_revenue_eur;
    } else {
      pr += row.revenue_eur;
      pc += row.d1_ap_conversions;
      par += row.d1_ap_customer_revenue_eur;
    }
  }
  return {
    d1_ecpx_eur: cc > 0 ? cr / cc : null,
    prev_d1_ecpx_eur: pc > 0 ? pr / pc : null,
    d1_roas: cr > 0 ? car / cr : null,
    prev_d1_roas: pr > 0 ? par / pr : null,
  };
}

function buildTimeseries(rows: DailyD1Row[]): {
  revenue: Timeseries;
  ecpx: Timeseries;
  roas: Timeseries;
  d7Ecpx: Timeseries;
  d7Roas: Timeseries;
  d30Ecpx: Timeseries;
  d30Roas: Timeseries;
} {
  const timestamps = rows.map(r => {
    const [year, month, day] = r.bid_date.split("-").map(Number);
    return new Date(year, month - 1, day);
  });
  return {
    revenue: { timestamps, data: rows.map(r => r.revenue_eur) },
    ecpx: {
      timestamps,
      data: rows.map(r => (r.d1_ap_conversions > 0 ? r.revenue_eur / r.d1_ap_conversions : null)),
    },
    roas: {
      timestamps,
      data: rows.map(r =>
        r.revenue_eur > 0 && r.d1_ap_customer_revenue_eur > 0 ? r.d1_ap_customer_revenue_eur / r.revenue_eur : null,
      ),
    },
    d7Ecpx: {
      timestamps,
      data: rows.map(r => (r.d7_ap_conversions > 0 ? r.revenue_eur / r.d7_ap_conversions : null)),
    },
    d7Roas: {
      timestamps,
      data: rows.map(r =>
        r.revenue_eur > 0 && r.d7_ap_customer_revenue_eur > 0 ? r.d7_ap_customer_revenue_eur / r.revenue_eur : null,
      ),
    },
    d30Ecpx: {
      timestamps,
      data: rows.map(r => (r.d30_ap_conversions > 0 ? r.revenue_eur / r.d30_ap_conversions : null)),
    },
    d30Roas: {
      timestamps,
      data: rows.map(r =>
        r.revenue_eur > 0 && r.d30_ap_customer_revenue_eur > 0 ? r.d30_ap_customer_revenue_eur / r.revenue_eur : null,
      ),
    },
  };
}

function tCritical95(df: number): number {
  const table: [number, number][] = [
    [1, 12.706],
    [2, 4.303],
    [3, 3.182],
    [4, 2.776],
    [5, 2.571],
    [6, 2.447],
    [7, 2.365],
    [8, 2.306],
    [9, 2.262],
    [10, 2.228],
    [12, 2.179],
    [15, 2.131],
    [20, 2.086],
    [25, 2.06],
    [30, 2.042],
    [40, 2.021],
    [60, 2.0],
    [120, 1.98],
  ];
  if (df >= 120) return 1.96;
  for (let i = 0; i < table.length - 1; i++) {
    if (df <= table[i + 1][0]) {
      const frac = (df - table[i][0]) / (table[i + 1][0] - table[i][0]);
      return table[i][1] + frac * (table[i + 1][1] - table[i][1]);
    }
  }
  return 1.96;
}

function linearRegression(points: [number, number][]): { slope: number; intercept: number } {
  const n = points.length;
  const sumX = points.reduce((s, p) => s + p[0], 0);
  const sumY = points.reduce((s, p) => s + p[1], 0);
  const sumXY = points.reduce((s, p) => s + p[0] * p[1], 0);
  const sumX2 = points.reduce((s, p) => s + p[0] * p[0], 0);
  const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
  return { slope, intercept: (sumY - slope * sumX) / n };
}

interface ScatterData {
  slope: number;
  intercept: number;
  r2: number;
  n: number;
  points: [number, number][];
  residuals: Timeseries;
  ciData: Array<{ x: number; yHat: number; lower: number; upper: number }>;
  xAxisMin: number;
  xAxisMax: number;
  trendStep: number;
}

interface Assessment {
  level: "good" | "caution" | "poor";
  reasons: string[];
}

function computeScatterData(rows: DailyD1Row[], getY: (r: DailyD1Row) => number | null): ScatterData | null {
  const validRows = rows.filter(r => getY(r) != null);
  if (validRows.length < 3) return null;

  const points: [number, number][] = validRows.map(r => [r.revenue_eur, getY(r)!]);
  const { slope, intercept } = linearRegression(points);

  const n = points.length;
  const xBar = points.reduce((s, p) => s + p[0], 0) / n;
  const yBar = points.reduce((s, p) => s + p[1], 0) / n;
  const SST = points.reduce((s, p) => s + (p[1] - yBar) ** 2, 0);
  const SSE = points.reduce((s, p) => s + (p[1] - (slope * p[0] + intercept)) ** 2, 0);
  const r2 = SST > 0 ? Math.max(0, 1 - SSE / SST) : 0;

  const Sxx = points.reduce((s, p) => s + (p[0] - xBar) ** 2, 0);
  const se = n > 2 ? Math.sqrt(SSE / (n - 2)) : 0;
  const t = tCritical95(n - 2);

  const minX = Math.min(...points.map(p => p[0]));
  const maxX = Math.max(...points.map(p => p[0]));
  const xAxisMin = minX * 0.8;
  const xAxisMax = maxX * 1.2;

  const numTrendPoints = 200;
  const trendStep = (xAxisMax - xAxisMin) / (numTrendPoints - 1);
  const ciData = Array.from({ length: numTrendPoints }, (_, i) => {
    const x = xAxisMin + i * trendStep;
    const yHat = slope * x + intercept;
    const hw = t * se * Math.sqrt(1 + 1 / n + (x - xBar) ** 2 / Sxx);
    return { x, yHat, lower: yHat - hw, upper: yHat + hw };
  });

  const residualTimestamps = validRows.map(r => {
    const [year, month, day] = r.bid_date.split("-").map(Number);
    return new Date(year, month - 1, day);
  });
  const residualValues = validRows.map(r => getY(r)! - (slope * r.revenue_eur + intercept));

  return {
    slope,
    intercept,
    r2,
    n,
    points,
    ciData,
    xAxisMin,
    xAxisMax,
    trendStep,
    residuals: { timestamps: residualTimestamps, data: residualValues },
  };
}

function buildScatterOption(
  data: ScatterData,
  yLabel: string,
  fmtY: (v: number) => string,
  tooltipLabel: string,
  extraTooltipLine?: (x: number, yHat: number) => string | null,
): EChartsOption {
  const { points, ciData, xAxisMin, trendStep } = data;
  const fmtX = (v: number) =>
    new Intl.NumberFormat("en-GB", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v);

  return {
    animation: false,
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "cross", label: { show: false } },
      extraCssText: tooltipCss,
      formatter: (params: unknown) => {
        const series = params as Array<{ seriesName: string; seriesType: string; value: [number, number] }>;
        const trend = series.find(s => s.seriesName === "Trend");
        const actual = series.find(s => s.seriesType === "scatter");
        const x = trend?.value[0] ?? actual?.value[0] ?? 0;
        const idx = Math.round((x - xAxisMin) / trendStep);
        const ci = ciData[Math.max(0, Math.min(ciData.length - 1, idx))];
        let html = `Revenue: ${fmtX(x)}`;
        if (actual) html += `<br/>Actual ${tooltipLabel}: ${fmtY(actual.value[1])}`;
        if (trend) {
          html += `<br/>Projected ${tooltipLabel}: ${fmtY(trend.value[1])}`;
          const extra = extraTooltipLine?.(x, trend.value[1]);
          if (extra) html += `<br/>${extra}`;
          html += `<br/>95% PI: ${fmtY(Math.max(0, ci.lower))} – ${fmtY(ci.upper)}`;
        }
        return html;
      },
    },
    xAxis: {
      type: "value",
      name: "Revenue (EUR)",
      nameLocation: "middle",
      nameGap: 30,
      min: data.xAxisMin,
      max: data.xAxisMax,
      axisLabel: { formatter: fmtX },
    },
    yAxis: {
      type: "value",
      name: yLabel,
      nameLocation: "end",
      nameGap: 12,
      axisLabel: { formatter: fmtY, margin: 12 },
    },
    grid: { ...defaultGrid, left: 80, bottom: 50 },
    series: [
      {
        name: "PI Upper",
        type: "line",
        data: ciData.map(d => [d.x, d.upper] as [number, number]),
        showSymbol: false,
        lineStyle: { color: "#BFBDA7", type: "dotted", width: 1, opacity: 0.5 },
        tooltip: { show: false },
        emphasis: { disabled: true },
      },
      {
        name: "PI Lower",
        type: "line",
        data: ciData.map(d => [d.x, Math.max(0, d.lower)] as [number, number]),
        showSymbol: false,
        lineStyle: { color: "#BFBDA7", type: "dotted", width: 1, opacity: 0.5 },
        tooltip: { show: false },
        emphasis: { disabled: true },
      },
      {
        name: "Trend",
        type: "line",
        data: ciData.map(d => [d.x, d.yHat] as [number, number]),
        showSymbol: false,
        lineStyle: { color: "#8C8B7C", type: "dashed" },
      },
      { name: "Daily", type: "scatter", data: points, symbolSize: 7, itemStyle: { color: "#111" } },
    ],
  } satisfies EChartsOption;
}

function assessExtrapolation(
  slope: number,
  r2: number,
  slopeExpectedPositive: boolean,
  xmrResiduals: Xmr | null,
  metricName: string,
): Assessment {
  const reasons: string[] = [];
  let level: "good" | "caution" | "poor" = "good";

  if (slopeExpectedPositive && slope < 0) {
    level = "poor";
    reasons.push(
      `More budget is correlated with fewer ${metricName} — this is logically inconsistent and suggests external factors (e.g. seasonality or audience changes) dominated the data window. Projections based on this data would be unreliable.`,
    );
  }

  const r2Pct = (r2 * 100).toFixed(0);
  if (r2 < 0.3) {
    if (level !== "poor") level = "poor";
    reasons.push(
      `Budget accounts for only ${r2Pct}% of the day-to-day variation in ${metricName}. Other factors dominate, so the trendline should not be used to project outcomes at different spend levels.`,
    );
  } else if (r2 < 0.6) {
    if (level === "good") level = "caution";
    reasons.push(
      `Budget accounts for ${r2Pct}% of the variation in ${metricName}. The trendline gives a rough guide, but projections carry meaningful uncertainty.`,
    );
  } else {
    reasons.push(
      `Budget accounts for ${r2Pct}% of the variation in ${metricName} — the relationship is strong enough to project outcomes at different spend levels.`,
    );
  }

  const anomalyCount = xmrResiduals?.anomalies?.length ?? 0;
  if (anomalyCount > 0) {
    if (level === "good") level = "caution";
    reasons.push(
      `The residuals chart shows ${anomalyCount} period${anomalyCount > 1 ? "s" : ""} where the budget–${metricName} relationship was unusually far from the trend. This may indicate the relationship shifted during the window, which weakens confidence in the projection.`,
    );
  } else if (level === "good") {
    reasons.push(
      "The residuals are stable over time, confirming the relationship has been consistent across the data window.",
    );
  }

  return { level, reasons };
}

// Revenue stability thresholds — calibrate as needed
const CV_CAUTION = 0.3;
const CV_POOR = 0.5;
const NEFF_CAUTION = 0.66;
const NEFF_POOR = 0.33;
const ANOMALY_CAUTION = 1;
const ANOMALY_POOR = 3;

function assessRevenueStability(xmrRevenue: Xmr, revenueTs: Timeseries): Assessment {
  const reasons: string[] = [];
  let level: "good" | "caution" | "poor" = "good";

  const values = revenueTs.data.filter((v): v is number => v != null);
  const n = values.length;
  if (n < 3) return { level: "poor", reasons: ["Too few data points to assess stability."] };

  // Coefficient of Variation
  const mean = values.reduce((s, v) => s + v, 0) / n;
  const std = Math.sqrt(values.reduce((s, v) => s + (v - mean) ** 2, 0) / n);
  const cv = mean > 0 ? std / mean : 0;
  const cvPct = (cv * 100).toFixed(0);

  if (cv > CV_POOR) {
    level = "poor";
    reasons.push(
      `Daily revenue varies by ${cvPct}% relative to its mean (CV = ${cv.toFixed(2)}). This level of volatility makes ratio metrics like eCPX and ROAS unreliable — day-to-day swings in spend dominate the signal.`,
    );
  } else if (cv > CV_CAUTION) {
    if (level === "good") level = "caution";
    reasons.push(
      `Daily revenue varies by ${cvPct}% relative to its mean (CV = ${cv.toFixed(2)}). Moderate volatility — ratio metrics should be interpreted with some caution.`,
    );
  } else {
    reasons.push(
      `Daily revenue varies by ${cvPct}% relative to its mean (CV = ${cv.toFixed(2)}) — spend is stable enough for ratio analysis.`,
    );
  }

  // Effective Sample Size
  const sumR = values.reduce((s, v) => s + v, 0);
  const sumR2 = values.reduce((s, v) => s + v * v, 0);
  const nEff = sumR2 > 0 ? (sumR * sumR) / sumR2 : 0;
  const nEffRatio = nEff / n;
  const nEffStr = nEff.toFixed(1);

  if (nEffRatio < NEFF_POOR) {
    if (level !== "poor") level = "poor";
    reasons.push(
      `Effective sample size is ${nEffStr} of ${n} days (${(nEffRatio * 100).toFixed(0)}%). A few high-spend days dominate the aggregate — performance metrics are driven by those days rather than a broad sample.`,
    );
  } else if (nEffRatio < NEFF_CAUTION) {
    if (level === "good") level = "caution";
    reasons.push(
      `Effective sample size is ${nEffStr} of ${n} days (${(nEffRatio * 100).toFixed(0)}%). Spend is somewhat concentrated — performance metrics are weighted toward higher-spend days.`,
    );
  } else {
    reasons.push(
      `Effective sample size is ${nEffStr} of ${n} days (${(nEffRatio * 100).toFixed(0)}%) — spend is well-distributed across the window.`,
    );
  }

  // XMR Anomaly Density
  const anomalyCount = xmrRevenue.anomalies.length;
  if (anomalyCount >= ANOMALY_POOR) {
    if (level !== "poor") level = "poor";
    reasons.push(
      `Revenue triggered ${anomalyCount} anomaly signals in the XMR chart — the process is not in statistical control. Structural shifts in spend undermine the assumptions behind eCPX/ROAS analysis.`,
    );
  } else if (anomalyCount >= ANOMALY_CAUTION) {
    if (level === "good") level = "caution";
    reasons.push(
      `Revenue triggered ${anomalyCount} anomaly signal${anomalyCount > 1 ? "s" : ""} in the XMR chart. Some instability in the spend pattern — check whether this affects the period you care about.`,
    );
  } else {
    reasons.push("Revenue shows no anomaly signals — spend has been a stable, in-control process over the window.");
  }

  return { level, reasons };
}

const fmtEUR = (v: number) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v);
const fmtCpx = (v: number) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(v);
const fmtRoas = (v: number) => `${(v * 100).toFixed(1)}%`;
const fmtCpxLog = (v: number) => fmtCpx(Math.exp(v));
const fmtRoasLog = (v: number) => fmtRoas(Math.exp(v));

export default function BudgetDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { isAuthenticated, requiresAuth } = useAuth();
  const [summary, setSummary] = useState<BudgetSummary | null>(null);
  const [d1, setD1] = useState<D1Metrics | null>(null);
  const [dailyRows, setDailyRows] = useState<DailyD1Row[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    if (requiresAuth && !isAuthenticated) return;
    setIsLoading(true);
    setError(null);

    let summarySQL: string;
    try {
      summarySQL = buildSummarySQL(id);
    } catch (e) {
      setError(String(e));
      setIsLoading(false);
      return;
    }

    runBigQueryQuery(summarySQL)
      .then(summaryResults => {
        const s = (summaryResults[0] as unknown as BudgetSummary) ?? null;
        setSummary(s);
        if (!s?.max_date) return;

        const chartMaxDate = getChartMaxDate();
        let dailySQL: string;
        try {
          dailySQL = buildCohortedDailySQL(id, chartMaxDate);
        } catch (e) {
          setError(String(e));
          return;
        }

        return runBigQueryQuery(dailySQL).then(results => {
          const rows = results as unknown as DailyD1Row[];
          setDailyRows(rows);
          setD1(computeD1FromDaily(rows, chartMaxDate));
        });
      })
      .catch(err => setError(String(err)))
      .finally(() => setIsLoading(false));
  }, [id, isAuthenticated, requiresAuth]);

  const timeseries = useMemo(() => (dailyRows.length > 0 ? buildTimeseries(dailyRows) : null), [dailyRows]);
  const xmrRevenue = useMemo(() => (timeseries ? computeXmr(timeseries.revenue) : null), [timeseries]);
  const xmrEcpx = useMemo(() => (timeseries ? computeXmr(timeseries.ecpx, { transform: "log" }) : null), [timeseries]);
  const xmrRoas = useMemo(() => {
    if (!timeseries) return null;
    const nonNull = timeseries.roas.data.filter(v => v != null);
    if (nonNull.length < 3) return null;
    return computeXmr(timeseries.roas, { transform: "log" });
  }, [timeseries]);
  const kalmanEcpx = useMemo(() => {
    if (!xmrEcpx) return null;
    const Q = 0.1 * xmrEcpx.sigma ** 2;
    const R = xmrEcpx.sigma ** 2;
    return computeKalman(xmrEcpx.ts, { Q, R });
  }, [xmrEcpx]);
  const kalmanRoas = useMemo(() => {
    if (!xmrRoas) return null;
    const Q = 0.1 * xmrRoas.sigma ** 2;
    const R = xmrRoas.sigma ** 2;
    return computeKalman(xmrRoas.ts, { Q, R });
  }, [xmrRoas]);
  const revenueStability = useMemo(
    () => (xmrRevenue && timeseries ? assessRevenueStability(xmrRevenue, timeseries.revenue) : null),
    [xmrRevenue, timeseries],
  );
  const cohortCutoffs = useMemo(() => {
    const maxDate = getChartMaxDate();
    const d7Cut = new Date(addDays(maxDate, -7) + "T00:00:00").getTime();
    const d30Cut = new Date(addDays(maxDate, -30) + "T00:00:00").getTime();
    return { d7Cut, d30Cut };
  }, []);

  const cohortMarkLine = useMemo(
    () => ({
      silent: true,
      symbol: "none",
      label: { fontSize: 9, color: "#8C8B7C" },
      data: [
        {
          xAxis: cohortCutoffs.d7Cut,
          label: { formatter: "D7 full", position: "insideEndTop" },
          lineStyle: { color: "#D2C8FF", type: "dotted" as const, width: 1 },
        },
        {
          xAxis: cohortCutoffs.d30Cut,
          label: { formatter: "D30 full", position: "insideEndTop" },
          lineStyle: { color: "#BFBDA7", type: "dotted" as const, width: 1 },
        },
      ],
    }),
    [cohortCutoffs],
  );

  const cohortEcpxOption = useMemo<EChartsOption | null>(() => {
    if (!timeseries) return null;
    const ts = timeseries.ecpx.timestamps.map(t => t.getTime());
    return {
      animation: false,
      tooltip: { trigger: "axis", extraCssText: tooltipCss, valueFormatter: (v: unknown) => fmtCpx(Number(v)) },
      legend: { data: ["D1", "D7", "D30"] },
      grid: defaultGrid,
      xAxis: { type: "time", boundaryGap: false } as never,
      yAxis: { type: "value", axisLabel: { formatter: fmtCpx } },
      series: [
        {
          name: "D1",
          type: "line",
          data: ts.map((t, i) => [t, timeseries.ecpx.data[i]]),
          showSymbol: false,
          lineStyle: { color: "#1D1D1F" },
          itemStyle: { color: "#1D1D1F" },
          markLine: cohortMarkLine,
        } as never,
        {
          name: "D7",
          type: "line",
          data: ts.map((t, i) => [t, timeseries.d7Ecpx.data[i]]),
          showSymbol: false,
          lineStyle: { color: "#D2C8FF" },
          itemStyle: { color: "#D2C8FF" },
        },
        {
          name: "D30",
          type: "line",
          data: ts.map((t, i) => [t, timeseries.d30Ecpx.data[i]]),
          showSymbol: false,
          lineStyle: { color: "#BFBDA7" },
          itemStyle: { color: "#BFBDA7" },
        },
      ],
    };
  }, [timeseries, cohortMarkLine]);

  const cohortRoasOption = useMemo<EChartsOption | null>(() => {
    if (!timeseries) return null;
    const ts = timeseries.roas.timestamps.map(t => t.getTime());
    return {
      animation: false,
      tooltip: { trigger: "axis", extraCssText: tooltipCss, valueFormatter: (v: unknown) => fmtRoas(Number(v)) },
      legend: { data: ["D1", "D7", "D30"] },
      grid: defaultGrid,
      xAxis: { type: "time", boundaryGap: false } as never,
      yAxis: { type: "value", axisLabel: { formatter: fmtRoas } },
      series: [
        {
          name: "D1",
          type: "line",
          data: ts.map((t, i) => [t, timeseries.roas.data[i]]),
          showSymbol: false,
          lineStyle: { color: "#1D1D1F" },
          itemStyle: { color: "#1D1D1F" },
          markLine: cohortMarkLine,
        } as never,
        {
          name: "D7",
          type: "line",
          data: ts.map((t, i) => [t, timeseries.d7Roas.data[i]]),
          showSymbol: false,
          lineStyle: { color: "#D2C8FF" },
          itemStyle: { color: "#D2C8FF" },
        },
        {
          name: "D30",
          type: "line",
          data: ts.map((t, i) => [t, timeseries.d30Roas.data[i]]),
          showSymbol: false,
          lineStyle: { color: "#BFBDA7" },
          itemStyle: { color: "#BFBDA7" },
        },
      ],
    };
  }, [timeseries, cohortMarkLine]);

  const convScatterData = useMemo(
    () =>
      dailyRows.length > 0
        ? computeScatterData(dailyRows, r => (r.d1_ap_conversions > 0 ? r.d1_ap_conversions : null))
        : null,
    [dailyRows],
  );
  const roasScatterData = useMemo(
    () =>
      dailyRows.length > 0
        ? computeScatterData(dailyRows, r =>
            r.revenue_eur > 0 && r.d1_ap_customer_revenue_eur > 0 ? r.d1_ap_customer_revenue_eur / r.revenue_eur : null,
          )
        : null,
    [dailyRows],
  );

  const xmrConvResiduals = useMemo(
    () => (convScatterData ? computeXmr(convScatterData.residuals) : null),
    [convScatterData],
  );
  const xmrRoasResiduals = useMemo(
    () => (roasScatterData ? computeXmr(roasScatterData.residuals) : null),
    [roasScatterData],
  );

  // Conversions tooltip also shows projected eCPX derived from the same model (eCPX = budget ÷ conversions)
  const scatterConvOption = useMemo(
    () =>
      convScatterData
        ? buildScatterOption(
            convScatterData,
            "AP Conversions",
            v => Math.round(v).toLocaleString("en-GB"),
            "AP Conversions",
            (x, yHat) => (yHat > 0 ? `Projected eCPX: ${fmtCpx(x / yHat)}` : null),
          )
        : null,
    [convScatterData],
  );
  const scatterRoasOption = useMemo(
    () => (roasScatterData ? buildScatterOption(roasScatterData, "D1 AP ROAS", fmtRoas, "D1 AP ROAS") : null),
    [roasScatterData],
  );

  const convAssessment = useMemo(
    () =>
      convScatterData
        ? assessExtrapolation(convScatterData.slope, convScatterData.r2, true, xmrConvResiduals, "conversions")
        : null,
    [convScatterData, xmrConvResiduals],
  );
  const roasAssessment = useMemo(
    () =>
      roasScatterData
        ? assessExtrapolation(roasScatterData.slope, roasScatterData.r2, false, xmrRoasResiduals, "ROAS")
        : null,
    [roasScatterData, xmrRoasResiduals],
  );

  if (isLoading) return <div className="entity-list__loading">Loading…</div>;
  if (error)
    return (
      <div className="entity-list__error">
        <p>Something went wrong loading this budget. Please try refreshing the page.</p>
        <p className="entity-list__error-detail">{error}</p>
      </div>
    );

  const change = summary ? formatChange(summary.revenue_eur, summary.prev_revenue_eur) : null;
  const dateRange = summary?.max_date ? formatDateRange(summary.max_date) : null;
  const cpxChange = formatChangeCpx(d1?.d1_ecpx_eur ?? null, d1?.prev_d1_ecpx_eur ?? null);
  const roasChange = formatChangeRoas(d1?.d1_roas ?? null, d1?.prev_d1_roas ?? null);

  return (
    <div className="entity-detail budget-report">
      <div className="audience-report__topbar">
        <Link to="/budgets" className="entity-detail__back">
          ← Budgets
        </Link>
        <button className="audience-report__pdf-btn" onClick={() => window.print()}>
          Export PDF
        </button>
      </div>

      {/* Hero Header */}
      <HeroHeader
        eyebrow="Budget Report"
        name={summary?.budget_name ?? `Budget ${id}`}
        subtitle={summary?.audience_names}
        dateRange={dateRange}
      />

      {/* KPI Strip */}
      {(summary || d1) && (
        <div className="audience-kpi-strip">
          {summary && (
            <div className="audience-kpi-strip__item">
              <div className="entity-detail__kpi-label">Revenue</div>
              <div className="entity-detail__kpi-value">{formatEUR(summary.revenue_eur)}</div>
              {change && (
                <div className="entity-detail__kpi-change">
                  <span className={`entity-list__change entity-list__change--${change.up ? "up" : "down"}`}>
                    {change.label} vs prev 7d
                  </span>
                </div>
              )}
            </div>
          )}
          {d1?.d1_ecpx_eur != null && (
            <div className="audience-kpi-strip__item">
              <div className="entity-detail__kpi-label">Avg. D1 AP eCPX</div>
              <div className="entity-detail__kpi-value">{formatEURCpx(d1.d1_ecpx_eur)}</div>
              {cpxChange && (
                <div className="entity-detail__kpi-change">
                  <span className={`entity-list__change entity-list__change--${cpxChange.up ? "up" : "down"}`}>
                    {cpxChange.label} vs prev 7d
                  </span>
                </div>
              )}
            </div>
          )}
          {d1?.d1_roas != null && (
            <div className="audience-kpi-strip__item">
              <div className="entity-detail__kpi-label">Avg. D1 AP ROAS</div>
              <div className="entity-detail__kpi-value">{(d1.d1_roas * 100).toFixed(1)}%</div>
              {roasChange && (
                <div className="entity-detail__kpi-change">
                  <span className={`entity-list__change entity-list__change--${roasChange.up ? "up" : "down"}`}>
                    {roasChange.label} vs prev 7d
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Process Behavior card */}
      {(xmrRevenue || xmrEcpx || xmrRoas) && (
        <div className="entity-detail__card audience-section--pagebreak">
          <p className="entity-detail__card-label">Process Behavior — 28 days</p>
          {revenueStability && (
            <div className="entity-detail__assessment-grid">
              <div
                className={`entity-detail__assessment-card entity-detail__assessment-card--${revenueStability.level}`}
              >
                <div className="entity-detail__assessment-metric">Revenue Stability</div>
                <div className="entity-detail__assessment-level">{revenueStability.level.toUpperCase()}</div>
                <ul className="entity-detail__assessment-reasons">
                  {revenueStability.reasons.map((r, i) => (
                    <li key={i}>{r}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}
          {xmrRevenue && (
            <div className="entity-detail__chart-section">
              <h2 className="entity-detail__chart-title">Revenue (EUR)</h2>
              <XmrChart xmr={xmrRevenue} axisFormatter={fmtEUR} />
            </div>
          )}
          {xmrEcpx && (
            <div className="entity-detail__chart-section">
              <XmrChart xmr={xmrEcpx} kalman={kalmanEcpx ?? undefined} title="D1 AP eCPX" axisFormatter={fmtCpxLog} />
            </div>
          )}
          {xmrRoas && (
            <div className="entity-detail__chart-section">
              <XmrChart xmr={xmrRoas} kalman={kalmanRoas ?? undefined} title="D1 AP ROAS" axisFormatter={fmtRoasLog} />
            </div>
          )}
        </div>
      )}

      {/* Cohorted Performance Curves */}
      {(cohortEcpxOption || cohortRoasOption) && (
        <div className="entity-detail__card audience-section--pagebreak">
          <p className="entity-detail__card-label">Cohorted Performance — D1 / D7 / D30</p>
          <div className="entity-detail__chart-pair">
            {cohortEcpxOption && (
              <div className="entity-detail__chart-pair-item">
                <h2 className="entity-detail__chart-title">eCPX by Cohort Window</h2>
                <EChart option={cohortEcpxOption} height={350} />
              </div>
            )}
            {cohortRoasOption && (
              <div className="entity-detail__chart-pair-item">
                <h2 className="entity-detail__chart-title">ROAS by Cohort Window</h2>
                <EChart option={cohortRoasOption} height={350} />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Budget Impact Analysis card */}
      {(convScatterData || roasScatterData) && (
        <div className="entity-detail__card audience-section--pagebreak">
          <p className="entity-detail__card-label">Budget Impact Analysis</p>
          <p className="entity-detail__chart-subtitle">
            How reliably does historical spend predict performance outcomes?
          </p>

          {/* Assessment row — one card per independent metric */}
          <div className="entity-detail__assessment-grid">
            {convAssessment && convScatterData && (
              <div className={`entity-detail__assessment-card entity-detail__assessment-card--${convAssessment.level}`}>
                <div className="entity-detail__assessment-metric">AP Conversions &amp; eCPX</div>
                <div className="entity-detail__assessment-level">{convAssessment.level.toUpperCase()}</div>
                <ul className="entity-detail__assessment-reasons">
                  {convAssessment.reasons.map((r, i) => (
                    <li key={i}>{r}</li>
                  ))}
                </ul>
                <p className="entity-detail__assessment-note">
                  eCPX projections are derived from this model (eCPX = budget ÷ projected conversions) — hover the chart
                  below.
                </p>
              </div>
            )}
            {roasAssessment && roasScatterData && (
              <div className={`entity-detail__assessment-card entity-detail__assessment-card--${roasAssessment.level}`}>
                <div className="entity-detail__assessment-metric">D1 AP ROAS</div>
                <div className="entity-detail__assessment-level">{roasAssessment.level.toUpperCase()}</div>
                <ul className="entity-detail__assessment-reasons">
                  {roasAssessment.reasons.map((r, i) => (
                    <li key={i}>{r}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Scatter charts — Conversions and ROAS side by side */}
          <div className="entity-detail__chart-pair">
            {convScatterData && scatterConvOption && (
              <div className="entity-detail__chart-pair-item">
                <h2 className="entity-detail__chart-title">
                  AP Conversions vs Revenue
                  {convScatterData.slope < 0 && <span className="entity-detail__warning">⚠ Negative slope</span>}
                </h2>
                <p className="entity-detail__chart-subtitle">
                  R² = {(convScatterData.r2 * 100).toFixed(0)}% — hover trendline for projected conversions &amp; eCPX
                </p>
                <EChart option={scatterConvOption} height={350} />
              </div>
            )}
            {roasScatterData && scatterRoasOption && (
              <div className="entity-detail__chart-pair-item">
                <h2 className="entity-detail__chart-title">D1 AP ROAS vs Revenue</h2>
                <p className="entity-detail__chart-subtitle">
                  R² = {(roasScatterData.r2 * 100).toFixed(0)}% — hover trendline for projected ROAS
                </p>
                <EChart option={scatterRoasOption} height={350} />
              </div>
            )}
          </div>

          {/* Residuals — temporal stability of each model */}
          {(xmrConvResiduals || xmrRoasResiduals) && (
            <>
              <hr className="entity-detail__divider" />
              <p className="entity-detail__card-label">Model Residuals Over Time</p>
              <p className="entity-detail__chart-subtitle">
                Each point shows how far the actual outcome was from the model prediction on that day. Anomalies (red)
                indicate the budget–performance relationship shifted — if present, treat projections with caution.
              </p>
              <div className="entity-detail__chart-pair">
                {xmrConvResiduals && (
                  <div className="entity-detail__chart-pair-item">
                    <h2 className="entity-detail__chart-title">Conversions Residuals</h2>
                    <XmrChart xmr={xmrConvResiduals} axisFormatter={v => Math.round(v).toLocaleString("en-GB")} />
                  </div>
                )}
                {xmrRoasResiduals && (
                  <div className="entity-detail__chart-pair-item">
                    <h2 className="entity-detail__chart-title">ROAS Residuals</h2>
                    <XmrChart xmr={xmrRoasResiduals} axisFormatter={fmtRoas} />
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* Print-only footer */}
      <div className="audience-print-footer">
        {summary?.budget_name} — Budget Report — {dateRange}
      </div>
    </div>
  );
}
